import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { ShoppingBag, ArrowLeft, CreditCard, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productName = searchParams.get('product') || '';
  const productPrice = searchParams.get('price') || '';

  const [formData, setFormData] = useState({
    vorname: '',
    name: '',
    adresse: '',
    plz: '',
    wohnort: '',
    email: '',
    telefon: ''
  });

  const [agbAccepted, setAgbAccepted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.vorname.trim()) newErrors.vorname = 'Vorname ist erforderlich';
    if (!formData.name.trim()) newErrors.name = 'Name ist erforderlich';
    if (!formData.adresse.trim()) newErrors.adresse = 'Adresse ist erforderlich';
    if (!formData.plz.trim()) newErrors.plz = 'PLZ ist erforderlich';
    if (!formData.wohnort.trim()) newErrors.wohnort = 'Wohnort ist erforderlich';
    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }
    if (!formData.telefon.trim()) newErrors.telefon = 'Telefon ist erforderlich';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm() && agbAccepted) {
      try {
        // Save order to backend
        await axios.post(`${BACKEND_URL}/api/orders`, {
          vorname: formData.vorname,
          name: formData.name,
          adresse: formData.adresse,
          plz: formData.plz,
          wohnort: formData.wohnort,
          email: formData.email,
          telefon: formData.telefon,
          product_title: productName,
          product_price: parseFloat(productPrice) || 0
        });
        
        // Redirect to SumUp payment
        window.open('https://pay.sumup.com/b2c/QWGW89CE', '_blank');
      } catch (error) {
        console.error('Error saving order:', error);
        // Still proceed to payment even if saving fails
        window.open('https://pay.sumup.com/b2c/QWGW89CE', '_blank');
      }
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--cream-white)' }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <nav className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            <ShoppingBag className="w-8 h-8" style={{ color: 'var(--lv-brown)' }} />
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}>
                Love Luxe
              </h1>
              <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--warm-gray)', letterSpacing: '1.5px' }}>
                Second-Hand Luxury
              </p>
            </div>
          </div>
          <Button 
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </Button>
        </nav>
      </header>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-6 py-12">
        <Card className="border-0 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--soft-gray)' }}>
              <CreditCard className="w-8 h-8" style={{ color: 'var(--lv-brown)' }} />
            </div>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}>
              Bestellung abschliessen
            </CardTitle>
            {productName && (
              <p className="text-sm mt-2" style={{ color: 'var(--warm-gray)' }}>
                Produkt: <span className="font-medium" style={{ color: 'var(--charcoal)' }}>{productName}</span>
                {productPrice && <span className="ml-2 font-semibold" style={{ color: 'var(--lv-brown)' }}>CHF {productPrice}</span>}
              </p>
            )}
          </CardHeader>
          
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
                    Vorname *
                  </label>
                  <Input
                    type="text"
                    name="vorname"
                    value={formData.vorname}
                    onChange={handleInputChange}
                    placeholder="Max"
                    className={errors.vorname ? 'border-red-500' : ''}
                  />
                  {errors.vorname && <p className="text-red-500 text-xs mt-1">{errors.vorname}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
                    Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Muster"
                    className={errors.name ? 'border-red-500' : ''}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
                  Adresse *
                </label>
                <Input
                  type="text"
                  name="adresse"
                  value={formData.adresse}
                  onChange={handleInputChange}
                  placeholder="Musterstrasse 123"
                  className={errors.adresse ? 'border-red-500' : ''}
                />
                {errors.adresse && <p className="text-red-500 text-xs mt-1">{errors.adresse}</p>}
              </div>

              {/* PLZ and City */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
                    PLZ *
                  </label>
                  <Input
                    type="text"
                    name="plz"
                    value={formData.plz}
                    onChange={handleInputChange}
                    placeholder="6300"
                    className={errors.plz ? 'border-red-500' : ''}
                  />
                  {errors.plz && <p className="text-red-500 text-xs mt-1">{errors.plz}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
                    Wohnort *
                  </label>
                  <Input
                    type="text"
                    name="wohnort"
                    value={formData.wohnort}
                    onChange={handleInputChange}
                    placeholder="Zug"
                    className={errors.wohnort ? 'border-red-500' : ''}
                  />
                  {errors.wohnort && <p className="text-red-500 text-xs mt-1">{errors.wohnort}</p>}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
                  E-Mail-Adresse *
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="max.muster@example.ch"
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
                  Telefon *
                </label>
                <Input
                  type="tel"
                  name="telefon"
                  value={formData.telefon}
                  onChange={handleInputChange}
                  placeholder="+41 79 123 45 67"
                  className={errors.telefon ? 'border-red-500' : ''}
                />
                {errors.telefon && <p className="text-red-500 text-xs mt-1">{errors.telefon}</p>}
              </div>

              {/* Divider */}
              <div className="border-t pt-6" style={{ borderColor: 'var(--soft-gray)' }}>
                {/* AGB Checkbox */}
                <div className="flex items-start space-x-3 mb-6">
                  <Checkbox
                    id="agb"
                    checked={agbAccepted}
                    onCheckedChange={setAgbAccepted}
                    className="mt-1"
                  />
                  <label htmlFor="agb" className="text-sm cursor-pointer" style={{ color: 'var(--charcoal)' }}>
                    Ich akzeptiere die{' '}
                    <a 
                      href="/terms-of-service" 
                      target="_blank" 
                      className="underline hover:opacity-80"
                      style={{ color: 'var(--lv-brown)' }}
                    >
                      Allgemeinen Geschäftsbedingungen (AGB)
                    </a>
                    {' '}und die{' '}
                    <a 
                      href="/privacy-policy" 
                      target="_blank" 
                      className="underline hover:opacity-80"
                      style={{ color: 'var(--lv-brown)' }}
                    >
                      Datenschutzerklärung
                    </a>
                  </label>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={!agbAccepted}
                  className="w-full py-6 text-lg font-medium flex items-center justify-center gap-2"
                  style={{ 
                    backgroundColor: agbAccepted ? 'var(--lv-brown)' : 'var(--soft-gray)', 
                    color: agbAccepted ? 'white' : 'var(--warm-gray)',
                    cursor: agbAccepted ? 'pointer' : 'not-allowed'
                  }}
                >
                  <CreditCard className="w-5 h-5" />
                  Jetzt kaufen
                </Button>

                {/* External Payment Notice */}
                <div className="mt-4 p-4 rounded-lg text-center" style={{ backgroundColor: 'var(--soft-gray)' }}>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <ExternalLink className="w-4 h-4" style={{ color: 'var(--warm-gray)' }} />
                    <span className="text-sm font-medium" style={{ color: 'var(--charcoal)' }}>
                      Sichere Zahlung über SUMUP
                    </span>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--warm-gray)' }}>
                    Sie werden auf die externe Bezahlplattform <strong>SUMUP</strong> weitergeleitet, 
                    um die Zahlung sicher abzuschliessen.
                  </p>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Trust Badges */}
        <div className="mt-8 text-center">
          <p className="text-sm" style={{ color: 'var(--warm-gray)' }}>
            🔒 Sichere SSL-Verschlüsselung • ✓ 100% Authentifizierte Produkte • 📦 Versicherter Versand
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
